import styled from "styled-components"
import { NextSeo } from "next-seo"
import type { GetStaticPropsContext } from "next"
import dayjs from "dayjs"
import { decode } from "shopify-gid"

import { getSite } from "@lib/site"
import type { Site, WishlistItem as WItem } from "@lib/types"
import { getDictionary } from "@lib/dictionary"
import { getColorValue, getOptionValue } from "@lib/utils"

import { useCustomer, useCheckout, useUI, useTracking } from "@hooks/index"

import { FluidImage } from "@components/common"
import { Button } from "@components/ui"
import { AuthContainer, AccountNav, Title, Placeholder } from "@components/account"

export default function Wishlist({ site }: { site: Site }) {
  const { wishlist, deleteFromWishlist, getCurrencyPrice, formatCurrencyPrice, currency } = useCustomer()

  const { id, addLineItem } = useCheckout()
  const { openCart } = useUI()
  const { track, tikTokTrack } = useTracking()

  const addToBag = (wishlistItem: WItem) => {
    addLineItem({ quantity: 1, variantId: wishlistItem.id })
    openCart()

    track("Wishlist Product Added to Cart", {
      cart_id: decode(id).id,
      product_id: decode(wishlistItem.id).id,
      name: wishlistItem.productTitle,
      variant: wishlistItem?.title,
      sku: wishlistItem?.sku,
      price: parseFloat(wishlistItem?.priceV2?.amount),
      brand: "Haute Hijab",
      quantity: 1,
      image_url: wishlistItem?.image?.transformedSrc,
    })
    const currencyAdjustedPrice = wishlistItem?.presentmentPrices.edges.find((edge: {node: {price: {currencyCode: string}}}) => edge.node.price.currencyCode === currency)?.node.price.amount || null;
    tikTokTrack("AddToCart", {
      content_type: "product (from wishlist)",
      content_id: decode(wishlistItem?.id).id,
      content_name: wishlistItem?.title,
      currency: currency,
      price: currencyAdjustedPrice,
      value: currencyAdjustedPrice,
      quantity: 1
    })
  }

  const removeFromWishlist = (wishlistItem: WItem) => {
    deleteFromWishlist(wishlistItem.id)
    track("Product Removed from Wishlist", {
      product_id: decode(wishlistItem.id).id,
      name: wishlistItem.productTitle,
      variant: wishlistItem?.title,
      sku: wishlistItem?.sku,
      price: parseFloat(wishlistItem?.priceV2?.amount),
      brand: "Haute Hijab",
      quantity: 1,
      image_url: wishlistItem?.image?.transformedSrc,
    })
  }

  return (
    <>
      <NextSeo title="Wishlist" />
      <AuthContainer>
        <Body>
          <AccountNav />
          <WishlistSection>
            <Header>
              <Title>Wishlist</Title>
            </Header>
            {wishlist.length > 0 ? (
              <WishlistList>
                {wishlist?.map((item: WItem) => {
                  const available = item.quantityAvailable! > 0
                  return (
                    <WishlistItem key={item.id}>
                      <Thumbnail>
                        {item?.image?.transformedSrc && (
                          <Image
                            src={item.image.transformedSrc}
                            alt={item.image.altText}
                          />
                        )}
                        <ThumbnailInfo>
                          <Heading>{item.productTitle}</Heading>
                          <ThumbnailDetail>
                            <b>Color:</b> {getColorValue(item.selectedOptions)}
                          </ThumbnailDetail>
                          <ThumbnailDetail>
                            <b>Size:</b> {getOptionValue(item.selectedOptions)}
                          </ThumbnailDetail>
                          <ThumbnailDetail>
                            <b>Price:</b> {formatCurrencyPrice(getCurrencyPrice(item))}
                          </ThumbnailDetail>
                          <Mobile>
                            <Availability>
                              <b>
                                Availability:{" "}
                                {available ? "IN STOCK" : "SOLD OUT"}
                              </b>
                            </Availability>
                            <Remove
                              type="button"
                              onClick={() => removeFromWishlist(item)}
                            >
                              Remove
                            </Remove>
                          </Mobile>
                        </ThumbnailInfo>
                      </Thumbnail>
                      <Mobile>
                        <AddToBag
                          buttonTheme="ghostBlackReverse"
                          title="Add to bag"
                          boldTitle
                          buttonAlign="right"
                          onClick={() => addToBag(item)}
                          $disabled={!available}
                        />
                      </Mobile>
                      <UserAction>
                        <Heading>Date Added</Heading>
                        <Date>
                          {dayjs(item.dateAdded).format("MMMM Do, YYYY")}
                        </Date>
                        <Availability>
                          <b>
                            Availability:{" "}
                            {available ? "IN STOCK" : "SOLD OUT"}
                          </b>
                        </Availability>
                        <Remove
                          type="button"
                          onClick={() => removeFromWishlist(item)}
                        >
                          Remove
                        </Remove>
                        <AddToBag
                          buttonTheme="ghostBlackReverse"
                          title="Add to bag"
                          boldTitle
                          buttonAlign="right"
                          onClick={() => addToBag(item)}
                          $disabled={!available}
                        />
                      </UserAction>
                    </WishlistItem>
                  )
                })}
              </WishlistList>
            ) : (
              <Placeholder richText={site.wishlistPlaceholder} />
            )}
          </WishlistSection>
        </Body>
      </AuthContainer>
    </>
  )
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 14rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 0 15rem;
    flex-direction: row;
    padding: 7.2rem 12rem 7rem 9rem;
  }
`

const WishlistSection = styled.section`
  width: 100%;
  padding: 3rem 2rem 0;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding: 0;
  }
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Mobile = styled.div`
  line-height: 2.8rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: none;
  }
`

const WishlistList = styled.ul``

const WishlistItem = styled.li`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.color.black};
  padding: 3.5rem 0;

  &:last-child {
    border: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    flex-direction: row;
    justify-content: space-between;
  }
`

const Thumbnail = styled.div`
  display: flex;
`

const Image = styled(FluidImage)`
  width: 12rem;
  height: 18.5rem;
  margin-right: 2.8rem;
`

const ThumbnailInfo = styled.div``

const Heading = styled.div`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 2rem;
  line-height: 2.6rem;
  font-weight: 300;
  margin: 0 0 0.5rem;
`

const ThumbnailDetail = styled.div`
  font-size: 1.5rem;
  line-height: 2.8rem;
  color: ${({ theme }) => theme.color.greyDark};
`

const UserAction = styled.div`
  display: none;
  text-align: right;
  line-height: 2.8rem;
  font-size: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: block;
  }
`

const Date = styled.div`
  color: ${({ theme }) => theme.color.grey};
`

const Availability = styled.div`
  color: ${({ theme }) => theme.color.greyDark};
`

const Remove = styled.button`
  text-decoration: underline;
  color: ${({ theme }) => theme.color.grey};
`

const AddToBag = styled(Button)<any>`
  margin-top: 3rem;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  opacity: ${({ $disabled }) => ($disabled ? "0.2" : "1")};
  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin-top: 2rem;
  }
`

export async function getStaticProps({
  preview = false,
  locale = "en-US",
}: GetStaticPropsContext) {
  const site = await getSite({ preview, locale })
  const dictionary = await getDictionary({ preview, locale })

  return {
    props: {
      site,
      dictionary,
      isAccountPage: true,
      locale,
    },
  }
}
